<template>
  <div class="app-manage">
    <!--
      顶部说明：把「这个页面干什么 / iOS 为什么不给下载地址」写在最显眼处，
      避免同事把 iOS 也填上 apk 地址、或把版本号填成 "1.0-beta"。
    -->
    <el-alert class="tip" type="info" :closable="false" show-icon title="App 版本管理（应用内检测升级）">
      <template #default>
        <div class="tip-body">
          本页发布版本后，<b>App 端登录/启动时会自动检测</b>，发现新版本即弹窗展示「本次更新内容」。<br/>
          <span class="tip-warn">
            ① Android 弹窗可复制 apk 地址下载；<b>iOS 只弹提示、不提供下载动作</b>（App Store 分发，不做引导跳转）。<br/>
            ② 同一平台<b>只能有一条「启用」记录</b>，发布新版本会自动停用旧的（历史保留可回滚）。<br/>
            ③ 强制升级 = 用户点「稍后」也关不掉弹窗，<b>仅在致命 Bug 时使用</b>；正常发版请留空强制阈值。<br/>
            ④ 本表只管 App 升级；小程序端热更新走微信机制，与这里无关。
          </span>
        </div>
      </template>
    </el-alert>

    <!-- 当前生效版本提示：让发版人一眼看到「线上正在推的版本」 -->
    <el-alert
        v-if="activeRow"
        class="tip"
        :type="activeRow.isMandatory === 1 ? 'error' : 'warning'"
        :closable="false"
        show-icon
    >
      <template #default>
        <div class="tip-body">
          当前生效：<b>{{ activeRow.platform }} {{ activeRow.version }}</b>
          <el-tag v-if="activeRow.isMandatory === 1" type="danger" size="mini" style="margin-left: 6px">强制升级</el-tag>
          <el-tag v-else type="success" size="mini" style="margin-left: 6px">可跳过</el-tag>
          <span class="muted">（{{ activeRow.platform === 'iOS' ? 'iOS 只弹提示，无下载动作' : '低于 ' + (activeRow.forceVersion || activeRow.version) + ' 的版本将被强制升级' }}）</span>
        </div>
      </template>
    </el-alert>

    <!-- 列表区 -->
    <el-card class="list-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span class="card-title">版本记录</span>
          <div class="list-filters">
            <el-select v-model="query.platform" placeholder="全部平台" clearable style="width: 130px" @change="handleSearch">
              <el-option label="Android" value="Android"/>
              <el-option label="iOS" value="iOS"/>
            </el-select>
            <el-select v-model="query.status" placeholder="全部状态" clearable style="width: 120px" @change="handleSearch">
              <el-option label="启用" :value="1"/>
              <el-option label="停用" :value="0"/>
            </el-select>
            <el-input
                v-model="query.keyword"
                placeholder="版本号 / 更新内容"
                style="width: 200px"
                clearable
                @clear="handleSearch"
                @keyup.enter="handleSearch"
            />
            <el-button type="primary" @click="handleSearch">搜索</el-button>
            <el-button @click="handleReset">重置</el-button>
            <el-button type="primary" icon="el-icon-plus" @click="openDialog()">发布版本</el-button>
          </div>
        </div>
      </template>

      <el-table
          v-loading="tableLoading"
          :data="tableData"
          border
          stripe
          header-cell-class-name="table-header"
      >
        <el-table-column label="平台" width="100" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.platform === 'iOS' ? '' : 'success'" size="small">
              {{ scope.row.platform }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="version" label="版本号" width="120" align="center">
          <template #default="scope">
            <b>{{ scope.row.version }}</b>
          </template>
        </el-table-column>

        <el-table-column label="升级方式" width="130" align="center">
          <template #default="scope">
            <el-tooltip
                v-if="scope.row.isMandatory === 1"
                content="强制升级：用户无法跳过弹窗。低于「强制升级阈值」的用户点不掉、安装不了，务必确认是致命 Bug"
                placement="top"
            >
              <el-tag type="danger" size="small">强制升级</el-tag>
            </el-tooltip>
            <el-tag v-else type="info" size="small">可跳过</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="强制阈值" width="110" align="center">
          <template #default="scope">
            <code v-if="scope.row.forceVersion">{{ scope.row.forceVersion }}</code>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>

        <el-table-column label="包大小" width="100" align="center">
          <template #default="scope">
            <span v-if="scope.row.fileSize">{{ sizeText(scope.row.fileSize) }}</span>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>

        <el-table-column label="下载地址" min-width="230" show-overflow-tooltip>
          <template #default="scope">
            <a
                v-if="scope.row.downloadUrl"
                :href="scope.row.downloadUrl"
                target="_blank"
                rel="noopener"
                class="url-link"
            >{{ scope.row.downloadUrl }}</a>
            <el-tooltip
                v-else-if="scope.row.platform === 'iOS'"
                content="iOS 不提供下载动作，属预期：App Store 分发，App 内只弹提示"
                placement="top"
            >
              <span class="muted">iOS 只提示（无地址）</span>
            </el-tooltip>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>

        <el-table-column label="本次更新内容" min-width="240" show-overflow-tooltip>
          <template #default="scope">
            <span>{{ scope.row.publishNote || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="90" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="createUserName" label="发布人" width="100" show-overflow-tooltip/>
        <el-table-column prop="createTime" label="发布时间" width="170"/>

        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template #default="scope">
            <el-button type="text" size="small" @click="openDialog(scope.row)">编辑</el-button>

            <el-button
                v-if="scope.row.status === 1"
                type="text"
                size="small"
                style="color: #E6A23C"
                @click="handleStatus(scope.row, 0)"
            >停用</el-button>
            <el-button
                v-else
                type="text"
                size="small"
                style="color: #67C23A"
                @click="handleStatus(scope.row, 1)"
            >启用</el-button>

            <el-popconfirm
                v-if="scope.row.status !== 1"
                title="确认删除这条版本记录？删除后不可恢复"
                confirm-button-text="删除"
                cancel-button-text="取消"
                @confirm="handleDelete(scope.row)"
            >
              <template #reference>
                <el-button type="text" size="small" style="color: #F56C6C">删除</el-button>
              </template>
            </el-popconfirm>
            <el-tooltip v-else content="生效中的版本不能删除，请先停用" placement="top">
              <span class="disabled-text">删除</span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
            background
            layout="total, prev, pager, next, sizes"
            :total="pageTotal"
            :current-page="query.pageIndex"
            :page-size="query.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- 发布 / 编辑弹框 -->
    <el-dialog
        v-model="dialogVisible"
        :title="form.id ? '编辑版本' : '发布新版本'"
        width="620px"
        :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="平台" prop="platform">
          <el-radio-group v-model="form.platform" @change="onPlatformChange">
            <el-radio-button label="Android">Android</el-radio-button>
            <el-radio-button label="iOS">iOS</el-radio-button>
          </el-radio-group>
          <div class="form-hint">
            Android 走「弹窗 + 复制 apk 地址」；iOS 走「只弹提示」，下载地址留空即可。
          </div>
        </el-form-item>

        <el-form-item label="版本号" prop="version">
          <el-input v-model="form.version" placeholder="如 1.0.1" maxlength="32" clearable/>
          <div class="form-hint">点分数字格式，最多 4 段。用户端当前版本低于此值即弹窗提示。</div>
        </el-form-item>

        <el-form-item label="升级方式">
          <el-radio-group v-model="form.isMandatory">
            <el-radio :label="0">可跳过（推荐）</el-radio>
            <el-radio :label="1">强制升级</el-radio>
          </el-radio-group>
          <div class="form-hint">
            选「强制升级」时，下方「强制升级阈值」生效；选「可跳过」时该阈值会被忽略。
          </div>
        </el-form-item>

        <el-form-item v-if="form.isMandatory === 1" label="强制升级阈值" prop="forceVersion">
          <el-input v-model="form.forceVersion" placeholder="如 1.0.1（低于此值的用户弹窗无法关闭）" maxlength="32" clearable/>
          <div class="form-hint">留空则默认等于版本号，即「所有旧版本一律强制」。慎用。</div>
        </el-form-item>

        <!--
          APK 上传（仅 Android）：把「选安装包」和「填下载地址」合成一步。
          上传成功后 url 自动回填到下方「下载地址」，fileSize 自动算出包大小，
          发版人全程只需：打包 → 选文件 → 点确认发布。
        -->
        <el-form-item v-if="form.platform === 'Android'" label="安装包上传">
          <div class="up-row">
            <el-upload
                :show-file-list="false"
                :http-request="doUploadApk"
                :disabled="uploading"
                accept=".apk"
            >
              <el-button type="primary" plain :loading="uploading">
                {{ uploading ? ('上传中 ' + uploadPercent + '%') : (form.downloadUrl ? '重新选择 APK' : '选择 APK 上传') }}
              </el-button>
            </el-upload>
            <span v-if="form.fileSize" class="up-ok">
              ✔ 已上传 {{ form.fileName || 'apk' }} · {{ sizeText(form.fileSize) }}
            </span>
          </div>
          <div class="form-hint">
            上传成功后<b>自动填入下方「下载地址」与包大小</b>，不用再手工粘贴。存 MinIO 共享存储，单个 ≤100MB。
            重传会生成带时间戳的新文件，<b>不会覆盖</b>已发出的二维码所指向的包。
          </div>
        </el-form-item>

        <el-form-item label="下载地址" prop="downloadUrl">
          <el-input
              v-model="form.downloadUrl"
              :placeholder="form.platform === 'iOS' ? 'iOS 留空（只弹提示，不提供下载）' : '必填，apk 的 https 直链，如 https://.../yanhuo-1.0.1.apk'"
              :disabled="form.platform === 'iOS'"
              maxlength="512"
              clearable
          />
          <div class="form-hint">必须是 http/https 开头。App 端弹窗会把此地址复制到剪贴板，由用户粘贴到浏览器下载。</div>
        </el-form-item>

        <el-form-item label="本次更新内容" prop="publishNote">
          <el-input
              v-model="form.publishNote"
              type="textarea"
              :rows="5"
              maxlength="1000"
              show-word-limit
              placeholder="会原样展示在 App 弹窗里，建议每行一条：&#10;1. 修复学员列表加载缓慢&#10;2. 新增体测签到快捷入口"
          />
          <div class="form-hint">这段文字是用户唯一能看到的信息，请写「用户视角」而非「代码视角」。</div>
        </el-form-item>

        <el-form-item label="发布后状态">
          <el-switch
              v-model="form.status"
              :active-value="1"
              :inactive-value="0"
              active-color="#13ce66"
              inactive-color="#dcdfe6"
          />
          <span class="form-hint" style="margin-left: 10px">
            启用后立即对全体 App 用户生效；同平台其它版本会自动停用。
          </span>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleSubmit">确认发布</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {page, save, setStatus, delAppVersion, uploadApk} from '@/api/appVersion';

/** 版本号：点分数字，最多 4 段（与后端正则一致，前端先挡一次，少一次无谓的 400） */
const VERSION_RE = /^\d+(\.\d+){0,3}$/;

export default {
  name: 'AppManage',
  data() {
    return {
      dialogVisible: false,
      submitting: false,
      // apk 上传态：uploading 控制按钮 loading，uploadPercent 显示进度百分比
      uploading: false,
      uploadPercent: 0,
      formRef: null,
      query: {
        pageIndex: 1,
        pageSize: 20,
        platform: null,
        status: null,
        keyword: ''
      },
      tableData: [],
      pageTotal: 0,
      tableLoading: false,
      form: this.emptyForm(),
      rules: {
        platform: [{required: true, message: '请选择平台', trigger: 'change'}],
        version: [
          {required: true, message: '请填写版本号', trigger: 'blur'},
          {
            validator: (rule, value, cb) => {
              if (!value || !String(value).trim()) return cb();
              if (!VERSION_RE.test(String(value).trim())) {
                return cb(new Error('版本号必须是点分数字格式，如 1.0.1'));
              }
              cb();
            },
            trigger: 'blur'
          }
        ],
        forceVersion: [
          {
            validator: (rule, value, cb) => {
              // 仅在「强制升级 + 填了阈值」时校验
              if (this.form.isMandatory !== 1 || !value || !String(value).trim()) return cb();
              const fv = String(value).trim();
              if (!VERSION_RE.test(fv)) return cb(new Error('强制升级阈值也必须是点分数字格式'));
              // 阈值不得高于版本号，否则语义矛盾（比要升的版本还高 → 谁都升不了）
              if (this.cmp(fv, String(this.form.version).trim()) > 0) {
                return cb(new Error('强制升级阈值不能高于版本号'));
              }
              cb();
            },
            trigger: 'blur'
          }
        ],
        downloadUrl: [
          {
            validator: (rule, value, cb) => {
              // iOS 不需要地址
              if (this.form.platform === 'iOS') return cb();
              if (!value || !String(value).trim()) return cb(new Error('Android 必须填写 apk 下载地址'));
              const u = String(value).trim();
              if (!/^https?:\/\//i.test(u)) return cb(new Error('下载地址必须以 http:// 或 https:// 开头'));
              cb();
            },
            trigger: 'blur'
          }
        ]
      }
    };
  },
  computed: {
    /** 当前生效版本（status=1）：列表里 id 最大（后端 orderByDesc(id)）的那条 */
    activeRow() {
      const actives = (this.tableData || []).filter(r => r.status === 1);
      if (!actives.length) return null;
      return actives.reduce((a, b) => (a.id > b.id ? a : b));
    }
  },
  created() {
    this.getData();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        platform: 'Android',
        version: '',
        forceVersion: '',
        downloadUrl: '',
        // 上传通道回填项（手工粘地址时保持空即可）
        fileSize: null,
        fileName: '',
        publishNote: '',
        isMandatory: 0,
        status: 1
      };
    },

    /** 点分版本号比较：a>b 返回 1，=0 相同，<0 a 旧于 b（非法段按 0） */
    cmp(a, b) {
      const pa = String(a || '').split('.');
      const pb = String(b || '').split('.');
      const len = Math.max(pa.length, pb.length);
      for (let i = 0; i < len; i++) {
        const na = parseInt(pa[i], 10) || 0;
        const nb = parseInt(pb[i], 10) || 0;
        if (na > nb) return 1;
        if (na < nb) return -1;
      }
      return 0;
    },

    /** 列表 */
    getData() {
      this.tableLoading = true;
      page({
        pageIndex: this.query.pageIndex,
        pageSize: this.query.pageSize,
        platform: this.query.platform,
        status: this.query.status,
        keyword: this.query.keyword
      })
          .then(res => {
            this.tableLoading = false;
            if (res && res.code === 200) {
              this.tableData = (res.data && res.data.records) || [];
              // ⚠️ 本项目分页返回体总数字段是 dataCount，不是 total
              this.pageTotal = (res.data && res.data.dataCount) || 0;
            } else {
              this.$message.error((res && res.message) || '获取列表失败');
            }
          })
          .catch(() => {
            this.tableLoading = false;
          });
    },

    handleSearch() {
      this.query.pageIndex = 1;
      this.getData();
    },

    handleReset() {
      this.query.platform = null;
      this.query.status = null;
      this.query.keyword = '';
      this.query.pageIndex = 1;
      this.getData();
    },

    handlePageChange(val) {
      this.query.pageIndex = val;
      this.getData();
    },

    handleSizeChange(val) {
      this.query.pageSize = val;
      this.query.pageIndex = 1;
      this.getData();
    },

    /** 打开弹框：不传 row = 新建发布；传 row = 编辑 */
    openDialog(row) {
      this.form = row
          ? {
              id: row.id,
              platform: row.platform,
              version: row.version,
              forceVersion: row.forceVersion || '',
              downloadUrl: row.downloadUrl || '',
              fileSize: row.fileSize || null,
              fileName: row.fileName || '',
              publishNote: row.publishNote || '',
              isMandatory: row.isMandatory === 1 ? 1 : 0,
              status: row.status === 1 ? 1 : 0
          }
          : this.emptyForm();
      this.dialogVisible = true;
      // 等弹框渲染出来再清校验，否则上一次的红色提示会残留
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.clearValidate();
      });
    },

    /** 平台切换：iOS 清空地址与上传产物（iOS 只提示不下载，留着会污染数据） */
    onPlatformChange() {
      if (this.form.platform === 'iOS') {
        this.form.downloadUrl = '';
        this.form.fileSize = null;
        this.form.fileName = '';
      }
    },

    /**
     * 选文件后立即上传（el-upload 的 http-request 钩子）。
     * 不走 el-upload 自带的 action，因为要带 token 并复用统一 request 实例
     * （否则 401 拦截、MinIO 域名归一这些全局逻辑都享受不到）。
     */
    doUploadApk(opt) {
      const raw = opt && opt.file;
      if (!raw) {
        return;
      }
      if (!/\.apk$/i.test(raw.name || '')) {
        this.$message.error('请选择 .apk 安装包');
        if (opt.onError) opt.onError(new Error('not apk'));
        return;
      }
      this.uploading = true;
      this.uploadPercent = 0;
      uploadApk(
          raw,
          { platform: this.form.platform, version: (this.form.version || '').trim() },
          (p) => { this.uploadPercent = p; }
      )
          .then(res => {
            this.uploading = false;
            if (res && res.code === 200 && res.data) {
              this.form.downloadUrl = res.data.url || '';
              this.form.fileSize = res.data.size || null;
              this.form.fileName = res.data.fileName || raw.name || '';
              this.$message.success('安装包上传成功，已自动填入下载地址');
              // 顺手替人认一下版本号（如 yanhuo-0.0.3.apk / 焱火体育_1.2.0.apk）
              // ⚠️ 只在「版本号还空着」时回填，绝不覆盖已经填好的值
              if (!(this.form.version || '').trim()) {
                const mv = String(res.data.fileName || raw.name || '').match(/(\d+\.\d+(?:\.\d+)?)/);
                if (mv) {
                  this.form.version = mv[1];
                  this.$message.info('已从文件名识别版本号：' + mv[1] + '，请核对后再发布');
                }
              }
              if (opt.onSuccess) opt.onSuccess(res);
            } else {
              this.$message.error((res && res.message) || '上传失败');
              if (opt.onError) opt.onError(res);
            }
          })
          .catch(() => {
            this.uploading = false;
            this.$message.error('上传失败，请检查网络后重试');
            if (opt.onError) opt.onError(new Error('upload failed'));
          });
    },

    /** 字节数 → 「28.6 MB」。与后端 formatSize 同口径，保证后台列表与下载页显示一致。 */
    sizeText(bytes) {
      const n = Number(bytes);
      if (!n || n <= 0) {
        return '-';
      }
      const mb = n / 1024 / 1024;
      if (mb >= 1024) {
        return (mb / 1024).toFixed(2) + ' GB';
      }
      return mb.toFixed(1) + ' MB';
    },

    handleSubmit() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return;
        // isMandatory=0 时把 forceVersion 置空：后端也会忽略，这里置空是为了数据干净
        const payload = {
          id: this.form.id,
          platform: this.form.platform,
          version: this.form.version.trim(),
          forceVersion: this.form.isMandatory === 1 ? (this.form.forceVersion || '').trim() : '',
          downloadUrl: this.form.platform === 'iOS' ? '' : this.form.downloadUrl.trim(),
          // 上传通道的产物：iOS 一律清空；Android 手工粘地址时可为空（后端会置 null）
          fileSize: this.form.platform === 'iOS' ? null : (this.form.fileSize || null),
          fileName: this.form.platform === 'iOS' ? '' : (this.form.fileName || ''),
          publishNote: this.form.publishNote ? this.form.publishNote.trim() : '',
          isMandatory: this.form.isMandatory,
          status: this.form.status
        };
        this.submitting = true;
        save(payload)
            .then(res => {
              this.submitting = false;
              if (res && res.code === 200) {
                this.$message.success(this.form.id ? '已保存' : '发布成功，App 端下次启动即生效');
                this.dialogVisible = false;
                this.getData();
              } else {
                this.$message.error((res && res.message) || '保存失败');
              }
            })
            .catch(() => {
              this.submitting = false;
              this.$message.error('保存失败，请稍后重试');
            });
      });
    },

    handleStatus(row, status) {
      if (status === 1 && row.platform === 'iOS') {
        this.$message.info('iOS 启用后，App 端将只弹窗提示有新版本（不提供下载）');
      }
      setStatus(row.id, status)
          .then(res => {
            if (res && res.code === 200) {
              this.$message.success(status === 1 ? '已启用' : '已停用');
              this.getData();
            } else {
              this.$message.error((res && res.message) || '操作失败');
            }
          })
          .catch(() => this.$message.error('操作失败'));
    },

    handleDelete(row) {
      delAppVersion(row.id)
          .then(res => {
            if (res && res.code === 200) {
              this.$message.success('已删除');
              this.getData();
            } else {
              this.$message.error((res && res.message) || '删除失败');
            }
          })
          .catch(() => this.$message.error('删除失败'));
    }
  }
};
</script>

<style scoped>
.app-manage {
  padding: 12px 16px;
}

.tip {
  margin-bottom: 12px;
}

.tip-body {
  line-height: 1.8;
  font-size: 13px;
}

.tip-warn {
  color: #E6A23C;
}

.list-card {
  margin-bottom: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.card-title {
  font-weight: 600;
  font-size: 15px;
}

.list-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
  margin-top: 2px;
}

/* 安装包上传行：按钮 + 「已上传 xxx」回执并排 */
.up-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.up-ok {
  font-size: 12.5px;
  color: #67C23A;
  line-height: 1.5;
}

.url-link {
  color: #409EFF;
  word-break: break-all;
}

.muted {
  color: #C0C4CC;
}

.disabled-text {
  color: #C0C4CC;
  cursor: not-allowed;
  font-size: 12px;
}

.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
